import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from './ui/ConfirmDialog';
import PageHeader from './ui/PageHeader';
import Button from './ui/Button';
import AdminMetricCard, { type AdminMetric } from './ui/AdminMetricCard';
import AdminSparkline from './ui/AdminSparkline';
import PageLoader from './ui/PageLoader';

type Expanded = Record<string, any>;

interface AnalyticsData {
  overview: Record<string, any>;
  users?: Record<string, any>;
  content?: Record<string, any>;
  registration_codes?: Record<string, any>;
  recent_activity?: Array<Record<string, any>>;
  assessment_review_queue?: Array<Record<string, any>>;
  import_metrics?: Record<string, any>;
  expanded?: Expanded;
  timeframe: string;
  generated_at: string;
}

const SECTIONS = [
  'overview',
  'growth',
  'vehicles',
  'engagement',
  'events',
  'revenue',
  'tickets',
  'clubs',
  'shops',
  'recognition',
  'search',
  'onboarding',
  'sponsorship',
  'moderation',
  'health',
] as const;

type Section = (typeof SECTIONS)[number];

const TIMEFRAMES = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '1y', label: 'Year' },
  { value: 'all', label: 'All time' },
];

function moneyMinor(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency}`;
  }
}

export const AdminDashboard: React.FC = () => {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const timeframe = searchParams.get('range') || '30d';
  const activeSection = (searchParams.get('section') as Section) || 'overview';

  const [seedConfirmOpen, setSeedConfirmOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [seedLoading, setSeedLoading] = useState(false);
  const [seedIncludeEvent, setSeedIncludeEvent] = useState(true);
  const [seedIncludeClubs, setSeedIncludeClubs] = useState(true);
  const [seedIncludeShops, setSeedIncludeShops] = useState(true);
  const [seedForce, setSeedForce] = useState(false);
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const [seedError, setSeedError] = useState<string | null>(null);

  const setRange = (range: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('range', range);
    setSearchParams(next, { replace: true });
  };

  const setSection = (section: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('section', section);
    setSearchParams(next, { replace: true });
  };

  const loadAnalytics = useCallback(async (tf = timeframe) => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getAdminAnalytics(tf);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [timeframe]);

  useEffect(() => {
    void loadAnalytics(timeframe);
  }, [timeframe, loadAnalytics]);

  const ex = analytics?.expanded;
  const alerts = (ex?.alerts || []) as Array<{ severity: string; message: string; href?: string }>;
  const health = ex?.system_health || {};

  const overviewMetrics: AdminMetric[] = useMemo(() => {
    if (!ex) return [];
    const g = ex.platform_growth || {};
    const eng = ex.engagement || {};
    const rev = ex.revenue || {};
    const mod = ex.moderation || {};
    const firstCurrency = (rev.gross_volume_by_currency || [])[0];
    return [
      ...(ex.growth_metrics || []).slice(0, 2),
      {
        label: 'Public vehicles',
        value: ex.vehicles?.public_vehicles ?? analytics?.overview?.public_vehicles,
        definition: 'Vehicles marked public',
        drilldown: '/admin',
      },
      {
        label: 'DAU / WAU / MAU',
        value: `${eng.dau ?? 0} / ${eng.wau ?? 0} / ${eng.mau ?? 0}`,
        definition: 'Active = last_login within 1 / 7 / 30 days',
      },
      {
        label: 'Paid orders',
        value: rev.available === false ? null : rev.orders_paid,
        unavailable: rev.available === false,
        definition: 'Orders with status paid in period',
        drilldown: '/admin/payments',
      },
      {
        label: firstCurrency
          ? `Gross (${firstCurrency.currency})`
          : 'Gross volume',
        value: firstCurrency ? firstCurrency.gross_minor : 0,
        unit: firstCurrency ? 'currency' : 'count',
        currency: firstCurrency?.currency || undefined,
        definition: 'Gross payment volume in minor units; currencies not combined',
        drilldown: '/admin/payments',
      },
      {
        label: 'Moderation backlog',
        value: mod.total_pending ?? 0,
        drilldown: '/admin/shops',
        definition: 'Pending shops, clubs, certs, contributors, performance',
      },
      {
        label: 'New mods',
        value: g.new_modifications ?? 0,
      },
    ];
  }, [ex, analytics]);

  const handleDataExport = async (type: 'users' | 'codes' | 'analytics') => {
    try {
      let data: any;
      let filename: string;
      switch (type) {
        case 'users':
          data = await apiService.getAdminUsers({ limit: 10000 });
          filename = `bilt-users-${new Date().toISOString().split('T')[0]}.json`;
          break;
        case 'codes':
          data = await apiService.getRegistrationCodes({ limit: 10000 });
          filename = `bilt-codes-${new Date().toISOString().split('T')[0]}.json`;
          break;
        default:
          data = analytics;
          filename = `bilt-analytics-${new Date().toISOString().split('T')[0]}.json`;
      }
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      showToast('Export ready', 'success');
    } catch (err: any) {
      showToast(err.message || `Unable to export ${type}.`, 'error');
    }
  };

  const runSeedDemo = async (dryRun: boolean) => {
    setSeedConfirmOpen(false);
    try {
      setSeedLoading(true);
      setSeedError(null);
      setSeedResult(null);
      const data = await apiService.seedDemoShowcase({
        action: 'seed',
        dry_run: dryRun,
        force: seedForce,
        include_event: seedIncludeEvent,
        include_clubs: seedIncludeClubs,
        include_shops: seedIncludeShops,
      });
      const lines = [
        data.message || (dryRun ? 'Dry run OK' : 'Seed complete'),
        data.password ? `Password: ${data.password}` : null,
        data.organizer ? `Organizer: @${data.organizer}` : null,
        data.event_slug ? `Event: /events/${data.event_slug}/archive` : null,
        typeof data.passport_encounters === 'number' ? `Passport encounters: ${data.passport_encounters}` : null,
        Array.isArray(data.shops) ? `Shops: ${data.shops.length}` : null,
        typeof data.favorites === 'number' ? `Favorites: ${data.favorites}` : null,
      ].filter(Boolean);
      setSeedResult(lines.join('\n'));
      if (!dryRun) await loadAnalytics();
    } catch (err: any) {
      setSeedError(err.message || 'Seed failed');
    } finally {
      setSeedLoading(false);
    }
  };

  const runClearDemo = async () => {
    setClearConfirmOpen(false);
    try {
      setSeedLoading(true);
      setSeedError(null);
      setSeedResult(null);
      const data = await apiService.seedDemoShowcase({
        action: 'clear',
        force: seedForce,
      });
      const lines = [
        data.message || 'Demo data cleared',
        typeof data.users === 'number' ? `Users removed: ${data.users}` : null,
        typeof data.vehicles === 'number' ? `Vehicles removed: ${data.vehicles}` : null,
      ].filter(Boolean);
      setSeedResult(lines.join('\n'));
      await loadAnalytics();
    } catch (err: any) {
      setSeedError(err.message || 'Clear failed');
    } finally {
      setSeedLoading(false);
    }
  };

  if (loading && !analytics) {
    return <PageLoader message="Loading platform overview…" />;
  }

  if (error && !analytics) {
    return (
      <div className="rounded-lg border border-rose-700/50 bg-rose-950/30 p-6" role="alert">
        <h2 className="text-rose-300 font-medium">Failed to load analytics</h2>
        <p className="text-gray-300 text-sm mt-1">{error}</p>
        <Button type="button" className="mt-4 min-h-[44px]" onClick={() => void loadAnalytics()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-gray-400">No analytics data available</p>;
  }

  return (
    <div className="space-y-6">
      <ConfirmDialog
        isOpen={seedConfirmOpen}
        title="Seed showcase demo data?"
        description={
          seedForce
            ? 'Force-seed on this environment. Existing demo_* accounts will be refreshed (safe upsert; no duplicates).'
            : 'Seeds demo users, cars, mod photos, Passport QR activity, shops, badges/clubs, and optionally a sample event. Safe to re-run.'
        }
        confirmLabel="Seed demo data"
        cancelLabel="Cancel"
        loading={seedLoading}
        loadingLabel="Seeding…"
        onCancel={() => setSeedConfirmOpen(false)}
        onConfirm={() => void runSeedDemo(false)}
      />
      <ConfirmDialog
        isOpen={clearConfirmOpen}
        title="Remove showcase demo data?"
        description="Deletes only demo_* / demo+*@bilt.local accounts and catalog-scoped clubs, shops, and the demo event. Production requires Force."
        confirmLabel="Remove test data"
        cancelLabel="Cancel"
        loading={seedLoading}
        loadingLabel="Removing…"
        onCancel={() => setClearConfirmOpen(false)}
        onConfirm={() => void runClearDemo()}
      />

      <PageHeader
        eyebrow="Admin"
        title="Platform overview"
        description="Operational snapshot of growth, engagement, events, revenue, and moderation. Amounts use integer minor units; currencies are not combined."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="analytics-timeframe" className="sr-only">
              Date range
            </label>
            <select
              id="analytics-timeframe"
              value={timeframe}
              onChange={(e) => setRange(e.target.value)}
              className="input min-h-[44px] text-sm"
            >
              {TIMEFRAMES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <Button type="button" variant="secondary" onClick={() => void loadAnalytics()} className="min-h-[44px]">
              Refresh
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span>
          Range: <span className="text-gray-300">{analytics.timeframe}</span>
        </span>
        <span aria-hidden="true">·</span>
        <span>
          TZ: <span className="text-gray-300">{ex?.reporting_timezone || health.timezone || 'UTC'}</span>
        </span>
        <span aria-hidden="true">·</span>
        <span>
          Updated:{' '}
          <time dateTime={analytics.generated_at} className="text-gray-300">
            {new Date(analytics.generated_at).toLocaleString()}
          </time>
        </span>
        {health.stripe_mode_hint ? (
          <>
            <span aria-hidden="true">·</span>
            <span>
              Stripe:{' '}
              <span className="text-gray-300">{health.stripe_mode_hint}</span>
            </span>
          </>
        ) : null}
        {error ? <span className="text-amber-300">Partial refresh failed — showing last good data</span> : null}
      </div>

      {alerts.length > 0 && (
        <section aria-labelledby="admin-alerts-heading" className="space-y-2">
          <h2 id="admin-alerts-heading" className="text-sm font-semibold text-white">
            Critical alerts
          </h2>
          <ul className="space-y-2">
            {alerts.map((a, i) => (
              <li
                key={i}
                className={`rounded-lg border px-4 py-3 text-sm ${
                  a.severity === 'critical'
                    ? 'border-rose-500/40 bg-rose-950/40 text-rose-100'
                    : 'border-amber-500/35 bg-amber-950/30 text-amber-100'
                }`}
              >
                <span className="font-medium">{a.severity === 'critical' ? 'Critical' : 'Warning'}</span>
                <span className="mx-2 text-white/40">—</span>
                {a.href ? (
                  <Link to={a.href} className="underline underline-offset-2 hover:text-white">
                    {a.message}
                  </Link>
                ) : (
                  a.message
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {overviewMetrics.map((m, i) => (
          <AdminMetricCard key={m.key || m.label + i} metric={m} />
        ))}
      </div>

      {ex?.trends && (
        <section aria-labelledby="trends-heading" className="form-section !p-4">
          <h2 id="trends-heading" className="text-sm font-semibold text-white mb-3">
            Daily trends ({timeframe})
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { key: 'users', label: 'Users' },
              { key: 'vehicles', label: 'Vehicles' },
              { key: 'modifications', label: 'Modifications' },
            ].map((t) => (
              <div key={t.key} className="rounded-lg border border-white/[0.06] p-3">
                <p className="text-xs text-gray-400 mb-2">{t.label}</p>
                <AdminSparkline points={ex.trends[t.key] || []} label={t.label} />
              </div>
            ))}
          </div>
        </section>
      )}

      <div
        className="flex flex-wrap gap-2"
        role="tablist"
        aria-label="Analytics sections"
      >
        {SECTIONS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeSection === key}
            onClick={() => setSection(key)}
            className={`px-3 py-1.5 rounded-lg text-sm min-h-[36px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
              activeSection === key
                ? 'bg-orange-500/15 text-orange-300 border border-orange-500/35'
                : 'text-gray-400 border border-white/[0.08] hover:bg-white/[0.06]'
            }`}
          >
            {key.charAt(0).toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      <div className="form-section !p-4 sm:!p-6" role="tabpanel">
        {activeSection === 'overview' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Jump to queues:{' '}
              <Link className="text-orange-300 underline" to="/admin/shops">
                Shops
              </Link>
              {' · '}
              <Link className="text-orange-300 underline" to="/admin/payments">
                Payments
              </Link>
              {' · '}
              <Link className="text-orange-300 underline" to="/admin/clubs">
                Clubs
              </Link>
              {' · '}
              <Link className="text-orange-300 underline" to="/admin/badges">
                Badges
              </Link>
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AdminMetricCard
                metric={{
                  label: 'Total users',
                  value: analytics.overview.total_users,
                  drilldown: '/admin/users',
                }}
              />
              <AdminMetricCard
                metric={{
                  label: 'Activation rate',
                  value: analytics.overview.user_activation_rate,
                  unit: 'percent',
                  definition: 'Active users / total users',
                }}
              />
              <AdminMetricCard
                metric={{
                  label: 'Avg mods / vehicle',
                  value: analytics.overview.avg_modifications_per_vehicle,
                }}
              />
              <AdminMetricCard
                metric={{
                  label: 'Active invite codes',
                  value: analytics.overview.active_codes,
                  drilldown: '/admin/codes',
                }}
              />
            </div>
          </div>
        )}

        {activeSection === 'growth' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(ex?.growth_metrics || []).map((m: AdminMetric) => (
              <AdminMetricCard key={m.key || m.label} metric={m} />
            ))}
          </div>
        )}

        {activeSection === 'vehicles' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AdminMetricCard metric={{ label: 'Total', value: ex?.vehicles?.total_vehicles }} />
              <AdminMetricCard metric={{ label: 'Public', value: ex?.vehicles?.public_vehicles }} />
              <AdminMetricCard metric={{ label: 'Private', value: ex?.vehicles?.private_vehicles }} />
              <AdminMetricCard metric={{ label: 'New in range', value: ex?.vehicles?.new_vehicles }} />
              <AdminMetricCard metric={{ label: 'With media', value: ex?.vehicles?.vehicles_with_media }} />
              <AdminMetricCard metric={{ label: 'Avg mods', value: ex?.vehicles?.avg_modifications }} />
            </div>
            {(ex?.vehicles?.by_make || []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <caption className="sr-only">Vehicles by make</caption>
                  <thead className="text-left text-gray-400">
                    <tr>
                      <th className="py-2 pr-4" scope="col">
                        Make
                      </th>
                      <th className="py-2" scope="col">
                        Count
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ex?.vehicles?.by_make || []).map((row: any) => (
                      <tr key={row.make} className="border-t border-white/[0.06] text-gray-200">
                        <td className="py-2 pr-4">{row.make}</td>
                        <td className="py-2">{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeSection === 'engagement' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard
              metric={{
                label: 'DAU',
                value: ex?.engagement?.dau,
                definition: 'Users with last_login in 24h',
              }}
            />
            <AdminMetricCard metric={{ label: 'WAU', value: ex?.engagement?.wau }} />
            <AdminMetricCard metric={{ label: 'MAU', value: ex?.engagement?.mau }} />
            <AdminMetricCard metric={{ label: 'Favorites', value: ex?.favorites_views?.total_favorites }} />
            <AdminMetricCard metric={{ label: 'New favorites', value: ex?.favorites_views?.new_favorites }} />
            <AdminMetricCard
              metric={{
                label: 'View events',
                value: ex?.favorites_views?.view_events,
                definition: 'analytics_events vehicle_view_counted',
              }}
            />
            <AdminMetricCard metric={{ label: 'Favorite opens', value: ex?.favorites_views?.favorite_open_events }} />
          </div>
        )}

        {activeSection === 'events' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AdminMetricCard metric={{ label: 'Events', value: ex?.events?.total_events, drilldown: '/admin/events' }} />
              <AdminMetricCard metric={{ label: 'New events', value: ex?.events?.new_events }} />
              <AdminMetricCard metric={{ label: 'Registrations', value: ex?.events?.total_registrations }} />
              <AdminMetricCard metric={{ label: 'Confirmed (period)', value: ex?.registration_funnel?.confirmed }} />
              <AdminMetricCard metric={{ label: 'Checked in', value: ex?.registration_funnel?.checked_in }} />
              <AdminMetricCard metric={{ label: 'Rejected', value: ex?.registration_funnel?.rejected }} />
              <AdminMetricCard metric={{ label: 'Withdrawn', value: ex?.registration_funnel?.withdrawn }} />
              <AdminMetricCard metric={{ label: 'No-show', value: ex?.registration_funnel?.no_show }} />
            </div>
            {ex?.registration_funnel?.by_status && (
              <p className="text-xs text-gray-500">
                Funnel is segmented by registration status; free vs paid modes should be compared separately in Payments.
              </p>
            )}
          </div>
        )}

        {activeSection === 'revenue' && (
          <div className="space-y-4">
            {!ex?.revenue?.available ? (
              <p className="text-sm text-gray-400">Payment tables unavailable — apply paid-events migrations.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <AdminMetricCard
                    metric={{
                      label: 'Orders created',
                      value: ex.revenue.orders_created,
                      drilldown: '/admin/payments',
                    }}
                  />
                  <AdminMetricCard metric={{ label: 'Orders paid', value: ex.revenue.orders_paid }} />
                  <AdminMetricCard metric={{ label: 'Failed / cancelled', value: ex.revenue.orders_failed }} />
                  <AdminMetricCard metric={{ label: 'Open disputes', value: ex.revenue.disputes_open }} />
                  <AdminMetricCard metric={{ label: 'Webhook failures', value: ex.revenue.webhook_failures }} />
                  <AdminMetricCard metric={{ label: 'Live paid (all-time)', value: ex.revenue.livemode_paid }} />
                  <AdminMetricCard metric={{ label: 'Test paid (all-time)', value: ex.revenue.testmode_paid }} />
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <caption className="text-left text-gray-400 mb-2">Gross volume by currency (minor units)</caption>
                    <thead className="text-left text-gray-400">
                      <tr>
                        <th className="py-2 pr-4" scope="col">
                          Currency
                        </th>
                        <th className="py-2 pr-4" scope="col">
                          Gross
                        </th>
                        <th className="py-2 pr-4" scope="col">
                          Platform fees
                        </th>
                        <th className="py-2" scope="col">
                          Orders
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ex.revenue.gross_volume_by_currency || []).map((row: any) => (
                        <tr key={row.currency} className="border-t border-white/[0.06] text-gray-200">
                          <td className="py-2 pr-4">{row.currency}</td>
                          <td className="py-2 pr-4">{moneyMinor(row.gross_minor, row.currency)}</td>
                          <td className="py-2 pr-4">{moneyMinor(row.platform_fees_minor, row.currency)}</td>
                          <td className="py-2">{row.orders}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500">{ex.revenue.unit_note}</p>
              </>
            )}
          </div>
        )}

        {activeSection === 'tickets' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {!ex?.tickets?.available ? (
              <p className="text-sm text-gray-400 col-span-full">Spectator ticket tables unavailable.</p>
            ) : (
              <>
                <AdminMetricCard metric={{ label: 'Tickets sold', value: ex.tickets.tickets_sold }} />
                <AdminMetricCard metric={{ label: 'Valid', value: ex.tickets.tickets_valid }} />
                <AdminMetricCard metric={{ label: 'Refunded', value: ex.tickets.tickets_refunded }} />
                <AdminMetricCard metric={{ label: 'Scans', value: ex.tickets.scans }} />
                <AdminMetricCard metric={{ label: 'Duplicate scans', value: ex.tickets.duplicate_scans }} />
              </>
            )}
          </div>
        )}

        {activeSection === 'clubs' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard metric={{ label: 'Clubs', value: ex?.clubs?.total_clubs, drilldown: '/admin/clubs' }} />
            <AdminMetricCard metric={{ label: 'Verified', value: ex?.clubs?.verified_clubs }} />
            <AdminMetricCard metric={{ label: 'New', value: ex?.clubs?.new_clubs }} />
            <AdminMetricCard metric={{ label: 'Active memberships', value: ex?.clubs?.active_memberships }} />
          </div>
        )}

        {activeSection === 'shops' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard
              metric={{
                label: 'Approved shops',
                value: ex?.shops?.shops?.approved,
                drilldown: '/admin/shops',
              }}
            />
            <AdminMetricCard metric={{ label: 'Pending review', value: ex?.shops?.shops?.pending_review, drilldown: '/admin/shops' }} />
            <AdminMetricCard metric={{ label: 'Approval rate', value: ex?.shops?.approval_rate_pct, unit: 'percent' }} />
            <AdminMetricCard metric={{ label: 'Avg approval hours', value: ex?.shops?.avg_approval_hours }} />
            <AdminMetricCard metric={{ label: 'Shops with builds', value: ex?.shops?.shops_with_builds }} />
            <AdminMetricCard metric={{ label: 'Pending contributors', value: ex?.community?.pending_contributors, drilldown: '/admin/expansion' }} />
          </div>
        )}

        {activeSection === 'recognition' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard metric={{ label: 'Achievement defs', value: ex?.achievements?.definitions, drilldown: '/admin/achievements' }} />
            <AdminMetricCard metric={{ label: 'Current issued', value: ex?.achievements?.current_issued }} />
            <AdminMetricCard metric={{ label: 'Newly issued', value: ex?.achievements?.newly_issued }} />
            <AdminMetricCard metric={{ label: 'Pending certs', value: ex?.moderation?.pending_certifications, drilldown: '/admin/certifications' }} />
          </div>
        )}

        {activeSection === 'search' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard metric={{ label: 'Search events', value: ex?.search?.search_events }} />
            <AdminMetricCard metric={{ label: 'Result opens', value: ex?.search?.result_opens }} />
            <AdminMetricCard metric={{ label: 'Zero-result events', value: ex?.search?.zero_result_events }} />
            <AdminMetricCard metric={{ label: 'Zero-result rate', value: ex?.search?.zero_result_rate_pct, unit: 'percent' }} />
            <p className="col-span-full text-xs text-gray-500">{ex?.search?.privacy_note}</p>
          </div>
        )}

        {activeSection === 'onboarding' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <AdminMetricCard metric={{ label: 'Started', value: ex?.onboarding?.started }} />
              <AdminMetricCard metric={{ label: 'Skipped', value: ex?.onboarding?.skipped }} />
              <AdminMetricCard metric={{ label: 'Completed', value: ex?.onboarding?.completed }} />
              <AdminMetricCard metric={{ label: 'Resumed', value: ex?.onboarding?.resumed }} />
              <AdminMetricCard metric={{ label: 'First vehicle', value: ex?.onboarding?.first_vehicle }} />
              <AdminMetricCard metric={{ label: 'First mod', value: ex?.onboarding?.first_modification }} />
              <AdminMetricCard metric={{ label: 'Skip rate', value: ex?.onboarding?.skip_rate_pct, unit: 'percent' }} />
              <AdminMetricCard metric={{ label: 'Completion rate', value: ex?.onboarding?.completion_rate_pct, unit: 'percent' }} />
            </div>
            <p className="text-xs text-gray-500">{ex?.onboarding?.coverage_note}</p>
          </div>
        )}

        {activeSection === 'sponsorship' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <AdminMetricCard metric={{ label: 'Advertisers', value: ex?.sponsorship?.advertisers, drilldown: '/admin/sponsorship' }} />
            <AdminMetricCard metric={{ label: 'Active campaigns', value: ex?.sponsorship?.active_campaigns }} />
            <AdminMetricCard
              metric={{
                label: 'Impressions',
                value: ex?.sponsorship?.impressions,
                definition: ex?.sponsorship?.impression_definition,
              }}
            />
            <AdminMetricCard metric={{ label: 'Clicks', value: ex?.sponsorship?.clicks }} />
            <AdminMetricCard metric={{ label: 'CTR', value: ex?.sponsorship?.ctr_pct, unit: 'percent' }} />
          </div>
        )}

        {activeSection === 'moderation' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <AdminMetricCard metric={{ label: 'Shop requests', value: ex?.moderation?.pending_shop_requests, drilldown: '/admin/shops' }} />
            <AdminMetricCard metric={{ label: 'Clubs', value: ex?.moderation?.pending_clubs, drilldown: '/admin/clubs' }} />
            <AdminMetricCard metric={{ label: 'Certifications', value: ex?.moderation?.pending_certifications, drilldown: '/admin/certifications' }} />
            <AdminMetricCard metric={{ label: 'Contributors', value: ex?.moderation?.pending_contributors, drilldown: '/admin/expansion' }} />
            <AdminMetricCard metric={{ label: 'Performance', value: ex?.moderation?.pending_performance, drilldown: '/admin/expansion' }} />
            <AdminMetricCard metric={{ label: 'Total pending', value: ex?.moderation?.total_pending }} />
          </div>
        )}

        {activeSection === 'health' && (
          <div className="space-y-3 text-sm text-gray-300">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <AdminMetricCard metric={{ label: 'App env', value: health.app_env }} />
              <AdminMetricCard metric={{ label: 'Stripe mode', value: health.stripe_mode_hint }} />
              <AdminMetricCard
                metric={{
                  label: 'Stripe secret',
                  value: health.stripe_secret_configured ? 'Configured' : 'Missing',
                }}
              />
              <AdminMetricCard
                metric={{
                  label: 'Webhook secret',
                  value: health.stripe_webhook_configured ? 'Configured' : 'Missing',
                }}
              />
              <AdminMetricCard metric={{ label: 'Webhook fails (7d)', value: health.webhook_failures_7d, drilldown: '/admin/payments' }} />
              <AdminMetricCard metric={{ label: 'Webhook events (24h)', value: health.webhook_events_24h }} />
            </div>
            <p className="text-xs text-gray-500">Secrets are never displayed — only configured / missing.</p>
            {health.paid_flags && (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs">
                {Object.entries(health.paid_flags).map(([k, v]) => (
                  <li key={k} className="flex justify-between border border-white/[0.06] rounded px-2 py-1.5">
                    <span className="text-gray-400">{k}</span>
                    <span className={v ? 'text-emerald-300' : 'text-gray-500'}>{v ? 'on' : 'off'}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {(ex?.top_lists?.top_vehicles_by_mods || []).length > 0 && activeSection === 'overview' && (
        <section aria-labelledby="top-builds-heading">
          <h2 id="top-builds-heading" className="text-lg font-semibold text-white mb-3">
            Most modified public builds
          </h2>
          <ul className="space-y-1 text-sm text-gray-300">
            {(ex?.top_lists?.top_vehicles_by_mods || []).slice(0, 8).map((v: any, i: number) => (
              <li key={i}>
                {v.year} {v.make} {v.model} · @{v.username} · {v.mod_count} mods
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="rounded-lg border border-white/[0.06] p-4 space-y-3" aria-labelledby="admin-tools-heading">
        <h2 id="admin-tools-heading" className="text-lg font-semibold text-white">
          Tools
        </h2>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => void handleDataExport('analytics')}>
            Export analytics JSON
          </Button>
          <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => void handleDataExport('users')}>
            Export users JSON
          </Button>
          <Button type="button" variant="secondary" className="min-h-[44px]" onClick={() => void handleDataExport('codes')}>
            Export codes JSON
          </Button>
        </div>
        <div className="pt-2 border-t border-white/[0.06] space-y-2">
          <p className="text-sm text-gray-400">
            Demo showcase data (local / staging only). Covers builds, mod photos, Passport QR scans, events, clubs, shops, favorites, and edge cases. Safe to re-seed; use Remove to wipe demo rows first.
          </p>
          <div className="flex flex-wrap gap-4 text-sm text-gray-300">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="checkbox-control" checked={seedIncludeEvent} onChange={(e) => setSeedIncludeEvent(e.target.checked)} />
              Include event
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="checkbox-control" checked={seedIncludeClubs} onChange={(e) => setSeedIncludeClubs(e.target.checked)} />
              Include clubs
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="checkbox-control" checked={seedIncludeShops} onChange={(e) => setSeedIncludeShops(e.target.checked)} />
              Include shops
            </label>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="checkbox-control" checked={seedForce} onChange={(e) => setSeedForce(e.target.checked)} />
              Force
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="secondary" className="min-h-[44px]" disabled={seedLoading} onClick={() => void runSeedDemo(true)}>
              Dry run
            </Button>
            <Button type="button" className="min-h-[44px]" disabled={seedLoading} onClick={() => setSeedConfirmOpen(true)}>
              Seed demo
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px] text-red-300 border-red-500/30 hover:bg-red-500/10"
              disabled={seedLoading}
              onClick={() => setClearConfirmOpen(true)}
            >
              Remove test data
            </Button>
          </div>
          {seedError ? <p className="text-sm text-rose-300" role="alert">{seedError}</p> : null}
          {seedResult ? <pre className="text-xs text-gray-400 whitespace-pre-wrap">{seedResult}</pre> : null}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
